import { call, put, select, takeLatest } from "redux-saga/effects";

import { getApplicationsFilters, getPublishersFilters } from "../../../AssetResults/config/filters";
import { fetchApplications } from "features/AssetResults/services/applicationsService";
import { fetchPublisherFilters } from "features/AssetResults/services/publishersService";
import { RStatus } from "../../../Application/types";
import { mapToContentItems } from "../../services/searchResultsMapperService";
import { fetchSearchResultsSearch } from "./searchResultsService";
import {
  type Application,
  type ContentItem,
  type FilterSlice,
  type SearchResult,
  type Publisher,
} from "../../../AssetResults/types";
import { getSearchResults, selectSearchFilters } from "./searchResultsSelectors";
import {
  err,
  fetchMore,
  got,
  gotFilters,
  initSearch,
  outOfResults,
  resetFilter,
  resetFilters,
  type SearchResultsState,
  updateFilters,
  updateSort,
  updateStatus,
  updateTerm,
} from "./searchResultsSlice";

export function* handleFetchNewSearchResults() {
  try {
    const state: SearchResultsState = yield select(getSearchResults);
    const { activePage, search, top } = state;
    const { appliedFilters, sortType, term } = search;
    const skip = (activePage - 1) * top;
    const searchResults: SearchResult[] = yield call(
      fetchSearchResultsSearch,
      term as string,
      appliedFilters,
      sortType,
      skip,
      top,
    );

    const contentItems = mapToContentItems(searchResults);

    yield call(testForOutOfResults, contentItems, top);
    yield put(got({ items: contentItems }));
  } catch (error: any) {
    yield put(err({ errorMessage: error?.message }));
  }
}

export function* handleFetchMoreSearchResults() {
  try {
    const state: SearchResultsState = yield select(getSearchResults);
    const { activePage, search, top, reachedEnd, items } = state;
    const { appliedFilters, sortType, term } = search;
    if (reachedEnd && items) {
      yield put(updateStatus({ status: RStatus.Got }));
      return;
    }
    const skip = (activePage - 1) * top;
    const searchResults: SearchResult[] = yield call(
      fetchSearchResultsSearch,
      term as string,
      appliedFilters,
      sortType,
      skip,
      top,
    );

    const contentItems = mapToContentItems(searchResults);

    yield call(testForOutOfResults, contentItems, top);
    yield put(got({ items: items.concat(contentItems) }));
  } catch (error: any) {
    yield put(err({ errorMessage: error?.message }));
  }
}

export function* testForOutOfResults(searchResults: ContentItem[], top: number) {
  if (searchResults.length < top) {
    yield put(outOfResults());
  }
}

export function* handleRequestFilterOptions() {
  try {
    const filters: FilterSlice = yield select(selectSearchFilters);
    const entityTypes = [...(filters.entityType ?? [])].map(x => x as string);
    const applications: Application[] = yield call(fetchApplications, entityTypes);
    const publishers: Publisher[] = yield call(fetchPublisherFilters, entityTypes);
    yield put(gotFilters({ key: "application", options: getApplicationsFilters(applications) }));
    yield put(gotFilters({ key: "publisher", options: getPublishersFilters(publishers) }));
  } catch (error: any) {
    yield put(err({ errorMessage: error?.message }));
  }
}

export function* searchResultsWatcherSaga() {
  yield takeLatest(fetchMore.type, handleFetchMoreSearchResults);
  yield takeLatest(
    [initSearch.type, updateSort.type, updateFilters.type, resetFilter.type, updateTerm.type, resetFilters.type],
    handleFetchNewSearchResults,
  );
  yield takeLatest([initSearch.type, updateFilters.type, resetFilters.type], handleRequestFilterOptions);
}
